import { useState, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, Download, Loader2, Package, Plus } from 'lucide-react';
import ContainerLayout from '@/shared/components/ContainerLayout';
import EmptyView from '@/shared/components/EmptyView';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import SearchBar from '@/shared/components/SearchBar';
import { Button } from '@/shared/ui/button';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import AddressDialog from '@/domains/addresses/dialogs/AddressDialog';
import { useUser } from '@/contexts/UserContext';
import { Address } from '@/shared/types/addressTypes';
import { useInfiniteAddresses, useDeleteAddress, useRemoveAddressFromOrganization } from '@/domains/addresses/services/addresses';
import AddressCard from '@/domains/addresses/components/AddressCard';
import AddAddressToAccountDialog from '@/domains/addresses/dialogs/AddAddressToAccountDialog';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Organization } from '@/shared/types/userTypes';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { Badge } from '@/shared/ui/badge';
import { Separator } from '@/shared/ui/separator';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const AddressesPage = () => {
  const { selectedRegionCode } = useUser();
  const { switchOrganizationAndNavigateToAccountDetails, switchOrganizationAndNavigateToServiceLineDetails } = useRouteNavigation();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.PLATFORM, Feature.ADDRESSES, Permission.MANAGE);

  const queryClient = useQueryClient();
  const {
    data: addressesInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch: refetchAddresses,
  } = useInfiniteAddresses(selectedRegionCode);
  const removeAddressFromOrganization = useRemoveAddressFromOrganization();
  const deleteAddress = useDeleteAddress();

  // State for generic alert dialog
  const [isAddAddressToOrganizationDialogOpen, setIsAddAddressToOrganizationDialogOpen] = useState(false);
  const [isCreateAddressDialogOpen, setIsCreateAddressDialogOpen] = useState(false);
  const [isUpdateAddressDialogOpen, setIsUpdateAddressDialogOpen] = useState(false);
  const [isRemoveAddressFromOrganizationDialogOpen, setIsRemoveAddressFromOrganizationDialogOpen] = useState(false);
  const [isDeleteAddressDialogOpen, setIsDeleteAddressDialogOpen] = useState(false);

  const pendingRemove = useRef<{ addressId: string; organizationId: string } | null>(null);
  const pendingDelete = useRef<{ addressId: string } | null>(null);

  const selectedAddress = useRef<Address | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const addresses = useMemo(() => {
    const pages = addressesInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: Address[] = [];
    for (const page of pages) {
      for (const a of page.results ?? []) {
        if (!seen.has(a.addressReferenceId)) {
          seen.add(a.addressReferenceId);
          merged.push(a);
        }
      }
    }
    return merged;
  }, [addressesInfiniteData?.pages]);

  const totalCount = addressesInfiniteData?.pages[0]?.totalCount ?? 0;

  const filteredAddresses = useMemo(() => {
    if (!searchTerm) return addresses;

    const searchLower = searchTerm.toLowerCase();
    return addresses?.filter(address => {
      return (
        address.formattedAddress?.toLowerCase().includes(searchLower) ||
        address.organization?.name?.toLowerCase().includes(searchLower) ||
        address.organization?.organizationId?.toLowerCase().includes(searchLower) ||
        address.serviceLines?.some(serviceLine => serviceLine.serviceLineName?.toLowerCase().includes(searchLower)) ||
        address.serviceLines?.some(serviceLine => serviceLine.serviceLineNumber?.toLowerCase().includes(searchLower))
      )
    });
  }, [addresses, searchTerm]);

  // Group terminals by account
  const groupedAddresses = useMemo(() => {
    if (!filteredAddresses) return {};

    const groups: Record<string, Address[]> = {};

    filteredAddresses.forEach(address => {
      const organizationKey = address.organization?.organizationId || 'unassigned';
      if (!groups[organizationKey]) {
        groups[organizationKey] = [];
      }
      groups[organizationKey].push(address);
    });

    return groups;
  }, [filteredAddresses]);

  const refetchAddressesData = useCallback(() => {
    if (selectedRegionCode) {
      void queryClient.resetQueries({ queryKey: ['region-addresses-infinite', selectedRegionCode] });
    }
  }, [queryClient, selectedRegionCode]);

  const onManageOrganization = async (organization: Organization) => {
    switchOrganizationAndNavigateToAccountDetails({ organization, accountDetailsTab: AccountDetailsTab.DASHBOARD });
  };

  // Create address
  const handleCreateAddress = () => {
    selectedAddress.current = null;
    setIsCreateAddressDialogOpen(true);
  };

  // Edit address
  const handleManageAddress = (address: Address) => {
    selectedAddress.current = address;
    setIsUpdateAddressDialogOpen(true);
  };

  // Add address to organization
  const handleAddAddressToOrganization = (address: Address) => {
    selectedAddress.current = address;
    setIsAddAddressToOrganizationDialogOpen(true);
  };

  // Remove address from organization
  const handleRemoveAddressFromOrganization = (address: Address) => {
    if (!address.organization?.organizationId) return;
    pendingRemove.current = { addressId: address.addressReferenceId, organizationId: address.organization.organizationId };
    setIsRemoveAddressFromOrganizationDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const handleConfirmRemoveAddressFromOrganization = async () => {
    if (!pendingRemove) return;
    try {
      await removeAddressFromOrganization.mutateAsync({
        addressId: pendingRemove.current.addressId,
        organizationId: pendingRemove.current.organizationId,
      });
      showSuccess(SUCCESS.ADDRESS.REMOVED_FROM_ACCOUNT);
      refetchAddressesData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ADDRESS.REMOVE_FROM_ACCOUNT);
    } finally {
      setIsRemoveAddressFromOrganizationDialogOpen(false);
      pendingRemove.current = null;
    }
  };

  // Delete address
  const handleDeleteAddress = (address: Address) => {
    pendingDelete.current = { addressId: address.addressReferenceId };
    setIsDeleteAddressDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const handleConfirmDeleteAddress = async () => {
    if (!pendingDelete) return;
    try {
      await deleteAddress.mutateAsync({ addressId: pendingDelete.current.addressId });
      showSuccess(SUCCESS.ADDRESS.DELETED);
      refetchAddressesData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ADDRESS.DELETE);
    } finally {
      setIsDeleteAddressDialogOpen(false);
      pendingDelete.current = null;
    }
  };

  const handleManageServiceLineForOrganization = async (organization: Organization, serviceLine: ServiceLine) => {
    switchOrganizationAndNavigateToServiceLineDetails(organization, serviceLine.serviceLineNumber);
  };

  // Handle export action
  const handleExportAction = () => {
    // If there is no data to export, show an alert
    if (!addresses || addresses.length === 0) {
      alert('No data to export');
      return;
    }

    // If the number of service lines is less than the total count, ask for confirmation
    if (addresses.length < totalCount) {
      if (confirm(`Only ${addresses.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
        handleExport();
      }
      return;
    }

    // Export the data
    handleExport();
  }

  // Export the data
  const handleExport = () => {
    const csvData = addresses.map(address => {
      return {
        'Datapool': selectedRegionCode,
        'Account Name': address.organization?.name || '-',
        'Account ID': address.organization?.organizationId || '-',
        'Address ID': address.addressReferenceId || '-',
        'Address': address.formattedAddress || '-',
        'Metadata': address.metadata || '-',
      };
    });

    exportToCsv(csvData, `addresses_${selectedRegionCode || 'region'}`);
  };
  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Addresses</h1>
            <p className="text-lg">Manage addresses for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            {handleCreateAddress && canManage && (
              <Button
                variant="default"
                size="default"
                className="gap-2"
                onClick={handleCreateAddress}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4" />
                Create Address
              </Button>
            )}
            {addresses && addresses.length > 0 && (
              <Button onClick={handleExportAction} className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </div>
        </div>
        {/* Search Bar */}
        {addresses && addresses.length > 0 && (
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search addresses by formatted address, service line name/number or account name..."
          />
        )}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading addresses...</p>
          </div>
        ) : (
          error ? (
            <EmptyView
              icon={Package}
              title="Error Loading Addresses"
              description="Error loading addresses at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchAddresses}
            />
          ) : (
            <>
              {/* Scrollable Content Section */}
              {filteredAddresses && filteredAddresses.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {Object.entries(groupedAddresses).map(([organizationKey, addresses]) => {
                    const organization = addresses[0]?.organization;
                    const organizationName = organization?.name || 'Unassigned Addresses';
                    const isUnassigned = organizationKey === 'unassigned';
                    const serviceLines = addresses.flatMap(address => address.serviceLines);

                    return (
                      <Card key={organizationKey} className="flex flex-col gap-4">
                        {/* Account Header */}
                        <CardHeader className={`p-4 pb-0 ${!isUnassigned && organization ? 'cursor-pointer' : ''}`}
                          onClick={() => !isUnassigned && organization && onManageOrganization(organization)}>
                          <div className="flex justify-between">
                            <div className="flex flex-row items-center gap-2">
                              <div className="w-12 h-12 rounded-lg flex items-center justify-center border">
                                <Building2 className="h-6 w-6" />
                              </div>
                              <div>
                                <CardTitle className="text-xl font-semibold">{organizationName}</CardTitle>
                              </div>
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              <Badge variant="default">
                                {serviceLines.length} service line{serviceLines.length > 1 ? 's' : ''}
                              </Badge>
                              {!isUnassigned && organization && <ChevronRight className="h-8 w-8" />}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="gap-4 space-y-4 pb-4 px-4">
                          {addresses.map((address) => (
                            <AddressCard
                              key={address.addressReferenceId}
                              address={address}
                              onManageAddress={canManage ? () => handleManageAddress(address) : undefined}
                              onDeleteAddress={canManage ? () => handleDeleteAddress(address) : undefined}
                              onAddAddressToOrganization={canManage ? () => handleAddAddressToOrganization(address) : undefined}
                              onRemoveAddressFromOrganization={canManage ? () => handleRemoveAddressFromOrganization(address) : undefined}
                              onManageServiceLine={(serviceLine) => handleManageServiceLineForOrganization(organization, serviceLine)}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}

                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{addresses.length}</span>
                        {' '}of <span className="font-bold">{totalCount}</span> addresses
                      </span>
                      {filteredAddresses.length !== addresses.length && (
                        <span className="text-sm">
                          ({filteredAddresses.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Package}
                  title={searchTerm ? "No Matching Addresses Found" : "No Addresses Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no addresses available."}
                  actionLabel={searchTerm && hasNextPage ? "Load More" : undefined}
                  onAction={searchTerm && hasNextPage ? () => void fetchNextPage() : undefined}
                />
              )}
            </>
          ))}
      </div>

      {selectedAddress && (
        <AddAddressToAccountDialog
          open={isAddAddressToOrganizationDialogOpen}
          onOpenChange={setIsAddAddressToOrganizationDialogOpen}
          onSuccess={refetchAddresses}
          address={selectedAddress.current}
          organizationsWithAddress={addresses?.filter(a => a.organization !== undefined).map(a => a.organization) || []}
          selectedRegionCode={selectedRegionCode}
        />
      )}

      {/* Create Address Dialog */}
      <AddressDialog
        open={isCreateAddressDialogOpen}
        onOpenChange={setIsCreateAddressDialogOpen}
        onSuccess={refetchAddresses}
        address={null}
        selectedRegionCode={selectedRegionCode}
      />

      {/* Update Address Dialog */}
      {selectedAddress && (
        <AddressDialog
          open={isUpdateAddressDialogOpen}
          onOpenChange={setIsUpdateAddressDialogOpen}
          onSuccess={refetchAddresses}
          address={selectedAddress.current}
          selectedRegionCode={selectedRegionCode}
        />
      )}

      {/* Generic Alert Dialog for confirming address removal */}
      <GenericAlertDialog
        open={isRemoveAddressFromOrganizationDialogOpen}
        onOpenChange={setIsRemoveAddressFromOrganizationDialogOpen}
        title="Remove Address from Organization"
        description="Are you sure you want to remove this address from the organization?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveAddressFromOrganization}
        loading={removeAddressFromOrganization.isPending}
      />

      {/* Generic Alert Dialog for confirming address deletion */}
      <GenericAlertDialog
        open={isDeleteAddressDialogOpen}
        onOpenChange={setIsDeleteAddressDialogOpen}
        title="Delete Address"
        description="Are you sure you want to delete this address?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteAddress}
        loading={deleteAddress.isPending}
      />
    </ContainerLayout >
  );
};

export default AddressesPage;
